import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { NeonText } from '../effects';
import { Button } from '../common';
import { useLanguage } from '@/i18n';
import { MdEmail, MdPhone } from 'react-icons/md';
import styles from './Contact.module.css';

export function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  return (
    <section id="contact" className={styles.contact} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <NeonText as="h2" color="pink" effect="pulse" className={styles.title} animate={false}>
            {t.contact.title}
          </NeonText>
        </motion.div>

        <div className={styles.content}>
          <motion.div
            className={styles.contactCard}
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className={styles.iconWrapper}>
              <MdEmail className={styles.icon} />
            </div>
            <h3 className={styles.cardTitle}>{t.contact.emailMe}</h3>
            <a href="mailto:contact@fernandoolea.com" className={styles.contactLink}>
              contact@fernandoolea.com
            </a>
          </motion.div>

          <motion.div
            className={styles.contactCard}
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className={styles.iconWrapper}>
              <MdPhone className={styles.icon} />
            </div>
            <h3 className={styles.cardTitle}>{t.contact.callMe}</h3>
            <a href="tel:+34636276078" className={styles.contactLink}>
              +34 636 27 60 78
            </a>
          </motion.div>
        </div>

        <motion.div
          className={styles.cta}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <p className={styles.ctaQuestion}>{t.contact.question}</p>
          <p className={styles.ctaSubtitle}>{t.contact.helpAs}</p>
          <div className={styles.roleTags}>
            <span className={styles.roleTagHighlight}>{t.contact.roles.techLead}</span>
            <span className={styles.roleTagHighlight}>{t.contact.roles.cto}</span>
            <span className={styles.roleTag}>{t.contact.roles.xrDev}</span>
            <span className={styles.roleTag}>{t.contact.roles.aiDev}</span>
            <span className={styles.roleTag}>{t.contact.roles.webDev}</span>
            <span className={styles.roleTag}>{t.contact.roles.frontend}</span>
            <span className={styles.roleTag}>{t.contact.roles.backend}</span>
          </div>
          <Button href="mailto:contact@fernandoolea.com" variant="primary" size="lg">
            {t.contact.letsTalk}
          </Button>
        </motion.div>

        {/* Social Links */}
        <motion.div
          className={styles.social}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <a
            href="https://github.com/FernandoOleaDev"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialLink}
            aria-label="GitHub"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.socialIcon}>
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/in/fernando-olea-lyceum/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialLink}
            aria-label="LinkedIn"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.socialIcon}>
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
